import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Clock, Circle, FileCode, Bot, Copy, Check, Loader2 } from 'lucide-react';
import api from '../../api.js';

function FeatureDetailModal({ feature, isOpen, onClose }) {
  const [subtasks, setSubtasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && feature) {
      loadSubtasks();
      // Reset prompt state when opening a new feature
      setGeneratedPrompt(null);
      setShowPrompt(false);
      setCopied(false);
    }
  }, [isOpen, feature]);

  // Generate AI prompt for this feature
  const handleGeneratePrompt = async () => {
    if (!feature?.id) return;

    setGeneratingPrompt(true);
    setCopied(false);

    try {
      const response = await api.aiPilot.generateFeaturePrompt(feature.id);
      if (response.ok && response.data) {
        setGeneratedPrompt(response.data);
        setShowPrompt(true);
      }
    } catch (error) {
      console.error('Error generating prompt:', error);
      alert('Failed to generate prompt. Please try again.');
    } finally {
      setGeneratingPrompt(false);
    }
  };

  // Copy prompt to clipboard
  const handleCopyPrompt = async () => {
    if (!generatedPrompt?.prompt) return;

    try {
      await navigator.clipboard.writeText(generatedPrompt.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const loadSubtasks = async () => {
    try {
      setLoading(true);
      const response = await api.featureSubtasks.getByFeature(feature.id);
      if (response.ok) {
        setSubtasks(response.data || []);
      }
    } catch (error) {
      console.error('Error loading subtasks:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !feature) return null;

  const getSubtasksByStatus = (status) => subtasks.filter(st => st.status === status);
  const doneSubtasks = getSubtasksByStatus('done');
  const inProgressSubtasks = getSubtasksByStatus('in-progress');
  const todoSubtasks = getSubtasksByStatus('todo');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in-progress':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'todo':
        return <Circle className="w-5 h-5 text-gray-400" />;
      default:
        return null;
    }
  };

  const SubtaskList = ({ title, items, icon: Icon, colorClass }) => {
    if (items.length === 0) return null;

    return (
      <div className="mb-6">
        <div className={`flex items-center gap-2 mb-3 font-semibold ${colorClass}`}>
          <Icon className="w-5 h-5" />
          <h4>{title} ({items.length})</h4>
        </div>
        <ul className="space-y-2">
          {items.map((subtask) => (
            <li key={subtask.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="mt-0.5">{getStatusIcon(subtask.status)}</div>
              <div className="flex-1">
                <p className="text-gray-900">{subtask.description}</p>
                {subtask.related_files && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                    <FileCode className="w-3 h-3" />
                    <span>{subtask.related_files}</span>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{feature.name}</h2>
              <div className="flex items-center gap-4">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                  {feature.progress}% Complete
                </span>
                <span className="text-sm text-gray-600">{feature.notes}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : subtasks.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Circle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No detailed breakdown available for this feature yet.</p>
              <p className="text-sm mt-1">Check back later for implementation details.</p>
            </div>
          ) : (
            <>
              {/* Progress Overview */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{doneSubtasks.length}</div>
                  <div className="text-sm text-green-800">Completed</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-yellow-600">{inProgressSubtasks.length}</div>
                  <div className="text-sm text-yellow-800">In Progress</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-gray-600">{todoSubtasks.length}</div>
                  <div className="text-sm text-gray-700">To Do</div>
                </div>
              </div>

              {/* Subtask Lists */}
              <SubtaskList
                title="Completed"
                items={doneSubtasks}
                icon={CheckCircle}
                colorClass="text-green-700"
              />
              <SubtaskList
                title="In Progress"
                items={inProgressSubtasks}
                icon={Clock}
                colorClass="text-yellow-700"
              />
              <SubtaskList
                title="To Do"
                items={todoSubtasks}
                icon={Circle}
                colorClass="text-gray-700"
              />
            </>
          )}
        </div>

        {/* Generated Prompt Section */}
        {showPrompt && generatedPrompt && (
          <div className="border-t border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-purple-900">Generated AI Prompt</h4>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  ~{generatedPrompt.metadata?.estimated_tokens || 0} tokens
                </span>
                <button
                  onClick={handleCopyPrompt}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy Prompt
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowPrompt(false)}
                  className="p-1.5 hover:bg-gray-200 rounded-lg"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="bg-white border border-purple-200 rounded-lg p-4 max-h-64 overflow-y-auto">
              <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                {generatedPrompt.prompt}
              </pre>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {feature.spec_reference && (
              <span className="text-sm text-gray-600">Reference: {feature.spec_reference}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGeneratePrompt}
              disabled={generatingPrompt}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingPrompt ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4" />
                  Generate Prompt
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeatureDetailModal;
